import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface PeakLoadCapacityPlanningProps {
  startDate: string;
  endDate: string;
}

const PeakLoadCapacityPlanning: React.FC<PeakLoadCapacityPlanningProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      const heatmapData = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => ({
        id: day,
        data: Array.from({ length: 24 }, (_, hour) => ({
          x: `${hour}:00`,
          y: Math.floor(20 + Math.random() * 80)
        }))
      }));

      setData({
        peak_hour: '14:00 - 15:00',
        peak_day: 'Wednesday',
        current_capacity: 10,
        recommended_capacity: 13,
        utilization_rate: 87,
        heatmapData
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (!data) return <Alert severity="info">No capacity data available</Alert>;

  const gaugeOptions: ApexOptions = {
    chart: { type: 'radialBar' },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        dataLabels: {
          name: { fontSize: '16px', offsetY: 120 },
          value: { fontSize: '24px', formatter: (val) => `${val}%` }
        }
      }
    },
    fill: { colors: [data.utilization_rate > 85 ? '#f44336' : '#4caf50'] },
    labels: ['Utilization Rate']
  };

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Peak Hour</Typography>
            <Typography variant="h5" color="primary">{data.peak_hour}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Peak Day</Typography>
            <Typography variant="h5" color="primary">{data.peak_day}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" variant="body2">Current Capacity</Typography>
            <Typography variant="h4" color="primary">{data.current_capacity} agents</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}><CardContent>
            <Typography color="textSecondary" variant="body2">Recommended</Typography>
            <Typography variant="h4" color="error">{data.recommended_capacity} agents</Typography>
            <Typography variant="caption">+3 needed</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Current Utilization</Typography>
            <Chart options={gaugeOptions} series={[data.utilization_rate]} type="radialBar" height={300} />
            <Alert severity={data.utilization_rate > 85 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              {data.utilization_rate > 85 ? 'Over-utilized! Add more agents' : 'Healthy utilization rate'}
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Conversation Volume Heatmap (By Day & Hour)</Typography>
            <Box height={350}>
              <ResponsiveHeatMap
                data={data.heatmapData}
                margin={{ top: 60, right: 90, bottom: 60, left: 90 }}
                valueFormat=">-.0f"
                axisTop={{ tickSize: 5, tickPadding: 5, tickRotation: -90 }}
                axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
                colors={{
                  type: 'diverging',
                  scheme: 'red_yellow_green',
                  divergeAt: 0.5,
                  minValue: 0,
                  maxValue: 100
                }}
                emptyColor="#555555"
                legends={[
                  {
                    anchor: 'bottom',
                    translateX: 0,
                    translateY: 30,
                    length: 400,
                    thickness: 8,
                    direction: 'row',
                    tickPosition: 'after',
                    tickSize: 3,
                    tickSpacing: 4,
                    tickOverlap: false,
                    title: 'Conversation Volume →',
                    titleAlign: 'start',
                    titleOffset: 4
                  }
                ]}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2">💡 Capacity Planning Recommendations</Typography>
        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
          <li><Typography variant="caption">Schedule {data.recommended_capacity - data.current_capacity} additional agents during peak hours</Typography></li>
          <li><Typography variant="caption">Consider flex/part-time staff for {data.peak_day}s</Typography></li>
          <li><Typography variant="caption">Target 75-85% utilization for optimal service levels</Typography></li>
        </ul>
      </Alert>
    </Box>
  );
};

export default PeakLoadCapacityPlanning;
